import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import { broadcastLogout, useAuthStore, type CurrentUser } from '@/stores/auth-store';

export function useCurrentUser() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const setUser = useAuthStore((state) => state.setUser);

  return useQuery({
    queryKey: queryKeys.currentUser,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/me');
      setUser(data ?? null);

      return data ?? null;
    },

    // Pas de jeton, pas de requête : évite un 401 systématique au premier rendu.
    enabled: accessToken !== null,

    // Le profil change rarement en cours de session : inutile de le rafraîchir souvent.
    staleTime: 5 * 60_000,
  });
}

/**
 * Connexion par mot de passe. Sur un compte 2FA, l'API ne renvoie aucun jeton : seulement
 * un jeton de défi que l'appelant doit présenter à `useVerifyTwoFactorLogin` avec le code
 * de l'application d'authentification. `setSession` n'est donc invoqué QUE si les jetons
 * sont bien présents dans la réponse.
 */
export function useLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await api.POST('/api/v1/auth/login', { body: credentials });

      return data!;
    },

    onSuccess: async (result) => {
      if (result.tokens === null || result.tokens === undefined) {
        return;
      }

      setSession(result.tokens.accessToken);

      // Le profil est rechargé immédiatement : c'est lui qui porte les permissions dont
      // dépend l'affichage de la navigation.
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/** Second temps de la connexion sur un compte 2FA : jeton de défi + code TOTP (ou de récupération). */
export function useVerifyTwoFactorLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { challengeToken: string; code: string }) => {
      const { data } = await api.POST('/api/v1/auth/login/2fa', { body: payload });

      return data!;
    },

    onSuccess: async (tokens) => {
      setSession(tokens.accessToken);
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/**
 * Fournisseurs de connexion externes réellement disponibles.
 *
 * La liste vient du SERVEUR et non d'une constante du frontend : lui seul sait si le
 * fournisseur est activé et complètement configuré sur cet environnement. C'est ce qui
 * évite d'afficher un bouton qui échouerait après avoir fait saisir des identifiants.
 */
export function useExternalAuthProviders() {
  return useQuery({
    queryKey: queryKeys.externalAuthProviders,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/external/providers');

      return data ?? [];
    },

    // La configuration ne bouge pas en cours de session : inutile de la redemander.
    staleTime: Infinity,
  });
}

/**
 * Premier temps de la connexion externe : demande l'URL d'autorisation.
 *
 * Le `state` et le `nonce` de la tentative NE transitent pas par ici — ils partent dans un
 * cookie `HttpOnly` que le JavaScript ne peut pas lire. C'est précisément ce qui empêche un
 * script injecté de fabriquer une demande de retour crédible.
 */
export function useStartExternalLogin() {
  return useMutation({
    mutationFn: async (payload: { provider: string }) => {
      const { data } = await api.POST('/api/v1/auth/external/start', { body: payload });

      return data!;
    },
  });
}

/** Second temps : le code rapporté du fournisseur est échangé contre une session. */
export function useCompleteExternalLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { provider: string; code: string; state: string }) => {
      const { data } = await api.POST('/api/v1/auth/external/callback', { body: payload });

      return data!;
    },

    // Même traitement que `useLogin` : sur un compte 2FA, l'API ne renvoie qu'un jeton de
    // défi, et il n'y a alors aucune session à ouvrir.
    onSuccess: async (result) => {
      if (result.tokens === null || result.tokens === undefined) {
        return;
      }

      setSession(result.tokens.accessToken);
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/** Premier temps de l'association depuis les paramètres : exige le mot de passe du compte. */
export function useStartExternalLink() {
  return useMutation({
    mutationFn: async (payload: { provider: string; password: string }) => {
      const { data } = await api.POST('/api/v1/auth/external/link/start', { body: payload });

      return data!;
    },
  });
}

/** Second temps : rattache l'identité externe au compte connecté. */
export function useCompleteExternalLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { provider: string; code: string; state: string }) => {
      await api.POST('/api/v1/auth/external/link/callback', { body: payload });
    },

    // Le profil porte le fournisseur associé : il alimente l'onglet Sécurité.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useUnlinkExternalProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { password: string }) => {
      await api.POST('/api/v1/auth/external/unlink', { body: payload });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/** Démarre l'enrôlement 2FA : renvoie le secret et l'URI otpauth:// à afficher en QR code. */
export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/auth/2fa/setup', {});

      return data!;
    },
  });
}

/**
 * Demande l'envoi du code de vérification par email, préalable à l'activation de la 2FA :
 * il prouve l'accès à la boîte mail avant que l'application d'authentification ne devienne
 * le second facteur du compte.
 */
export function useRequestTwoFactorEmailCode() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/2fa/email/request');
    },
  });
}

/** Confirme l'enrôlement avec le code email et le premier code TOTP, renvoie les codes de récupération. */
export function useEnableTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { emailCode: string; code: string }) => {
      const { data } = await api.POST('/api/v1/auth/2fa/enable', { body: payload });

      return data!;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/**
 * Désactive la 2FA. Exige le mot de passe du compte ET un code du second facteur (TOTP ou
 * code de récupération) : le mot de passe seul ne prouve pas la possession de l'application
 * d'authentification.
 */
export function useDisableTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { password: string; code: string }) => {
      await api.POST('/api/v1/auth/2fa/disable', { body: payload });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (payload: {
      companyName: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      jobTitle: string;
      phoneNumber: string;
      websiteUrl?: string | null;
      city?: string | null;
      countryCode?: string | null;
    }) => {
      const { data } = await api.POST('/api/v1/auth/register', { body: payload });

      return data!;
    },
  });
}

/** Valide le code à 4 chiffres reçu par email et active le compte. */
export function useConfirmEmail() {
  return useMutation({
    mutationFn: async (payload: { email: string; code: string }) => {
      await api.POST('/api/v1/auth/confirm-email', { body: payload });
    },
  });
}

/**
 * Demande un nouveau code de confirmation.
 *
 * Le serveur répond 204 même pour une adresse inconnue (protection contre l'énumération
 * de comptes) : l'interface ne peut donc rien en déduire, et se contente d'accuser
 * réception de la demande.
 */
export function useResendEmailConfirmation() {
  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      await api.POST('/api/v1/auth/confirm-email/resend', { body: payload });
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/logout', { body: { allSessions: false } });
    },

    // `onSettled` et non `onSuccess` : même si l'appel réseau échoue, l'utilisateur a
    // demandé à se déconnecter. Le laisser sur une interface authentifiée serait à la fois
    // déroutant et risqué sur un poste partagé.
    onSettled: () => {
      clear();
      queryClient.clear();
      broadcastLogout();
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      await api.POST('/api/v1/auth/password-reset/request', { body: payload });
    },
  });
}

/**
 * Premier temps du changement de mot de passe : vérifie le mot de passe actuel et
 * déclenche l'envoi d'un code de vérification par email.
 */
export function useRequestChangePassword() {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string }) => {
      await api.POST('/api/v1/auth/change-password/request', { body: payload });
    },
  });
}

/**
 * Second temps : présente le code reçu par email pour appliquer le nouveau mot de passe.
 * Révoque toutes les sessions (y compris la session courante) : on déconnecte donc
 * l'utilisateur localement après succès, comme sur `useLogout`.
 */
export function useChangePassword() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { code: string; newPassword: string }) => {
      await api.POST('/api/v1/auth/change-password/confirm', { body: payload });
    },

    onSuccess: () => {
      clear();
      queryClient.clear();
      broadcastLogout();
    },
  });
}

/**
 * Applique un profil renvoyé par l'API au store ET au cache de `/me`. L'API renvoie le
 * profil à jour en réponse à chaque modification : inutile de le re-demander.
 */
function useApplyCurrentUser() {
  const setUser = useAuthStore((state) => state.setUser);
  const queryClient = useQueryClient();

  return (user: CurrentUser) => {
    setUser(user);
    queryClient.setQueryData(queryKeys.currentUser, user);
  };
}

/** Paramètres › Profil : prénom, nom et poste de l'utilisateur connecté. */
export function useUpdateProfile() {
  const applyCurrentUser = useApplyCurrentUser();

  return useMutation({
    mutationFn: async (payload: { firstName: string; lastName: string; jobTitle: string }) => {
      const { data } = await api.PUT('/api/v1/auth/me', { body: payload });

      return data!;
    },

    onSuccess: applyCurrentUser,
  });
}

/**
 * Paramètres › Profil : coordonnées du compte. Réservé au propriétaire (`billing:manage`),
 * ces informations étant partagées par toute l'équipe.
 */
export function useUpdateCompanyDetails() {
  const applyCurrentUser = useApplyCurrentUser();

  return useMutation({
    mutationFn: async (payload: {
      companyName: string;
      phoneNumber: string;
      websiteUrl: string | null;
      city: string | null;
      countryCode: string;
    }) => {
      const { data } = await api.PUT('/api/v1/auth/me/company', { body: payload });

      return data!;
    },

    onSuccess: applyCurrentUser,
  });
}

/**
 * Paramètres › Préférences : fuseau horaire de référence du compte. Réservé au
 * propriétaire (`billing:manage`) — il donne leur sens aux plages de diffusion.
 */
export function useUpdateTimeZone() {
  const applyCurrentUser = useApplyCurrentUser();

  return useMutation({
    mutationFn: async (payload: { timeZoneId: string }) => {
      const { data } = await api.PUT('/api/v1/auth/me/time-zone', { body: payload });

      return data!;
    },

    onSuccess: applyCurrentUser,
  });
}
